/**
 * ConflictResolutionRow — shown in GroupSufficiencyCard when two or more
 * group-root matches share the exact same maximum confidence for a qit_id.
 *
 * Design decisions:
 *  - No auto-selection: the user must actively pick one candidate.
 *  - Selecting a candidate fires patchMatch on all OTHER candidates to set
 *    user_excluded=true, and on the chosen one to set
 *    user_excluded=false + user_confirmed=true.
 *  - Re-clicking an already-selected candidate is a no-op (single-select,
 *    cannot deselect once chosen — Apply All requires one winner).
 *  - Re-picking a different candidate reverses the exclusions.
 *  - Supports N candidates (not limited to 2).
 *  - After the user picks, the row stays as ConflictResolutionRow so they
 *    can change their mind (the selected option is visually highlighted).
 */
import React, { JSX, useState } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { ExtractionMatch } from 'src/types/aiFileUploadV2';
import { formatMatchValue } from '../utils/formatMatchValue';

export interface ConflictResolutionRowProps {
  questionName: string;
  candidates: ExtractionMatch[];
  /** Maps renter_file_id → original_filename for source attribution. */
  fileNamesById: Record<number, string>;
  onPatch: (
    fileId: number,
    matchId: number,
    patch: { user_confirmed?: boolean; user_excluded?: boolean }
  ) => Promise<ExtractionMatch | null>;
}

export function ConflictResolutionRow({
  questionName,
  candidates,
  fileNamesById,
  onPatch,
}: ConflictResolutionRowProps): JSX.Element {
  const theme = useTheme();

  // Derive selected match id from the candidates' current DB state.
  // A candidate is "selected" when user_excluded=false and user_confirmed=true.
  // If none match (no pick yet), selectedId is null.
  const initialSelectedId =
    candidates.find((c) => !c.user_excluded && c.user_confirmed)?.id ?? null;
  const [selectedId, setSelectedId] = useState<number | null>(
    initialSelectedId
  );
  const [busy, setBusy] = useState(false);

  const handlePick = async (chosen: ExtractionMatch): Promise<void> => {
    if (busy) {
      return;
    }
    if (chosen.id === selectedId) {
      return;
    } // already selected — no-op

    setBusy(true);
    try {
      // Mark all others excluded, mark chosen as included + confirmed.
      await Promise.all(
        candidates.map((c) => {
          if (c.id === chosen.id) {
            return onPatch(c.renter_file_id, c.id, {
              user_excluded: false,
              user_confirmed: true,
            });
          }
          return onPatch(c.renter_file_id, c.id, { user_excluded: true });
        })
      );
      setSelectedId(chosen.id);
    } finally {
      setBusy(false);
    }
  };

  const hasSelection = selectedId !== null;

  return (
    <Box sx={{ py: 0.75, minWidth: 0 }} data-testid="conflict-row">
      {/* Header row: question label */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 0.75,
          flexWrap: 'wrap',
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: theme.palette.text.secondary,
            flex: '1 1 auto',
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          {questionName}
        </Typography>

        {/* "Pick one" chip — only shown when no selection has been made yet */}
        {!hasSelection && (
          <Box
            sx={{
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: theme.palette.warning.main + '33',
              color: theme.palette.warning.dark,
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {busy && (
              <CircularProgress
                size={9}
                thickness={4}
                sx={{ color: 'inherit', mr: 0.25 }}
              />
            )}
            <span>Multiple answers — pick one</span>
          </Box>
        )}
      </Box>

      {/* Candidate list */}
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}
      >
        {candidates.map((candidate) => {
          const isSelected = candidate.id === selectedId;
          const raw =
            candidate.user_edited_value ??
            candidate.proposed_answer_text ??
            candidate.extracted_value;
          const displayValue = formatMatchValue(
            raw ?? '',
            candidate.value_type ?? candidate.qit?.question_type
          );
          const filename =
            fileNamesById[candidate.renter_file_id] ??
            `File #${candidate.renter_file_id}`;

          return (
            <Box
              key={candidate.id}
              onClick={() => void handlePick(candidate)}
              data-testid="conflict-candidate"
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: 0.75,
                border: '1.5px solid',
                borderColor: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.divider,
                borderRadius: 1.5,
                bgcolor: isSelected
                  ? theme.palette.primary.main + '14'
                  : theme.palette.background.paper,
                cursor: busy ? 'wait' : 'pointer',
                transition: 'border-color 0.15s, background-color 0.15s',
                minWidth: 0,
                '&:hover': {
                  borderColor: busy
                    ? undefined
                    : isSelected
                    ? theme.palette.primary.dark
                    : theme.palette.primary.light,
                },
              }}
            >
              <Checkbox
                checked={isSelected}
                size="small"
                disabled={busy}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0, mt: '1px', flexShrink: 0 }}
                inputProps={{
                  'aria-label': displayValue,
                }}
              />
              <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    overflowWrap: 'break-word',
                  }}
                >
                  {displayValue}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: theme.palette.text.secondary,
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={filename}
                >
                  from {filename}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ConflictResolutionRow;
